
import { PlatformValueSection as GenericPlatformValueSection } from "@/components/services/specializations/PlatformValueSection";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";

export const PlatformValueSection = () => {
  // Benefits data that matches the expected ValueBenefitProps interface
  const benefits = [
    {
      title: "Advanced Threat Protection",
      description: "Detect and respond to sophisticated threats with Microsoft Defender's AI-powered security."
    },
    {
      title: "Identity & Access Management",
      description: "Implement zero-trust security with conditional access and multi-factor authentication."
    },
    {
      title: "Data Loss Prevention (DLP)",
      description: "Protect sensitive information with automated policy enforcement across all Microsoft 365 services."
    },
    {
      title: "Endpoint Management",
      description: "Secure devices with Microsoft Intune's comprehensive management and security capabilities."
    },
    {
      title: "Cloud App Security",
      description: "Monitor and control cloud application usage with Microsoft Cloud App Security integration."
    },
    {
      title: "Information Protection",
      description: "Classify, label, and protect sensitive data automatically throughout its lifecycle."
    }
  ];

  // Secure score improvement data for the graph
  const secureScoreData = [
    { month: 'Jan', score: 35, benchmark: 32 },
    { month: 'Feb', score: 38, benchmark: 33 },
    { month: 'Mar', score: 45, benchmark: 34 },
    { month: 'Apr', score: 53, benchmark: 35 },
    { month: 'May', score: 62, benchmark: 36 },
    { month: 'Jun', score: 70, benchmark: 37 },
    { month: 'Jul', score: 78, benchmark: 38 },
    { month: 'Aug', score: 85, benchmark: 39 },
  ];

  // Custom rendering for the PlatformValueSection image
  const customImageComponent = (
    <Card className="relative p-6 shadow-xl bg-white border-0">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Microsoft Secure Score Improvement</h3>
          <p className="text-sm text-gray-500">Typical client results over 8 months</p>
        </div>
        <div className="flex items-center px-3 py-1 bg-green-100 rounded-full">
          <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
          <span className="text-sm font-medium text-green-600">+143%</span>
        </div>
      </div>
      
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={secureScoreData}
            margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickMargin={10}
            />
            <YAxis 
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickMargin={10}
              label={{ value: 'Secure Score', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748b', fontSize: 12 } }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}
              labelStyle={{ fontWeight: 'bold', marginBottom: '5px' }}
            />
            <Line 
              type="monotone" 
              dataKey="score" 
              name="Client Secure Score" 
              stroke="#6501E5" 
              strokeWidth={3}
              dot={{ fill: '#6501E5', r: 4 }}
              activeDot={{ r: 6, fill: '#6501E5', stroke: 'white', strokeWidth: 2 }}
            />
            <Line 
              type="monotone" 
              dataKey="benchmark" 
              name="Industry Average" 
              stroke="#A4B6E2" 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );

  return (
    <GenericPlatformValueSection 
      title="Unlock Hidden Security Value"
      subtitle="Microsoft 365"
      description="Most organizations use less than 40% of the security capabilities they already own in Microsoft 365. We help you maximize your existing investment."
      benefits={benefits}
      imageSrc="/lovable-uploads/b15e860f-0f58-46de-9fa5-1f5ec1be93cf.png"
      imageAlt="Microsoft 365 Security Dashboard"
      linkUrl="/get-started"
      linkText="Discover Your Security Potential"
      customImageComponent={customImageComponent}
    />
  );
};
